import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import PlaceIcon from '@mui/icons-material/Place';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import InputBase from '@mui/material/InputBase';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Chip from '@mui/material/Chip';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import * as api from '../api';
import { AuthenticationError, TripBuildError } from '../api';
import * as analytics from '../analytics';
import { TripMap, POIMap } from './maps';
import type { RouteData } from './maps';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import {
  AuthRequiredDialog,
  DeleteSegmentDialog,
  POIDetailsDialog,
  TripPricingDialog,
  AddPOIDialog,
  RebuildTripDialog,
  LoginDialog,
  RegisterDialog,
  ForgotPasswordDialog,
} from './dialogs';
import ShareTripDialog from './dialogs/ShareTripDialog';
import ManageSharesDialog from './dialogs/ManageSharesDialog';
import GroupIcon from '@mui/icons-material/Group';
import {
  DayHeader,
  DaySelector,
  DayActionsToolbar,
  DaySegmentsList,
} from './itinerary';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import RouteIcon from '@mui/icons-material/Route';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import SaveIcon from '@mui/icons-material/Save';
import ShareIcon from '@mui/icons-material/Share';
import Snackbar from '@mui/material/Snackbar';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import BuildIcon from '@mui/icons-material/Build';
import AddIcon from '@mui/icons-material/Add';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import LoginIcon from '@mui/icons-material/Login';

// Define libraries outside component to prevent reloading on each render
const GOOGLE_MAPS_LIBRARIES: Libraries = ['places'];

function TripItinerary({ tripData, onBack, onReset, isAuthenticated, onTripSaved, onLoginSuccess = () => {}, isSavedTrip = false, isSystemTrip = false }) {
  const [segments, setSegments] = useState(null);
  const [pois, setPois] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPOI, setSelectedPOI] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedItineraryDay, setSelectedItineraryDay] = useState('1'); // Track which day is selected for itinerary
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);
  const [mapView, setMapView] = useState('trip'); // 'trip' or 'poi'
  const [cities, setCities] = useState([]);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('success');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedTripName, setEditedTripName] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [currentTripData, setCurrentTripData] = useState(tripData);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  const [isTripSaved, setIsTripSaved] = useState(false);
  const [isCurrentlySystemTrip, setIsCurrentlySystemTrip] = useState(isSystemTrip);
  const [loginPromptOpen, setLoginPromptOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState(false);
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [savePromptDismissed, setSavePromptDismissed] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggedSegment, setDraggedSegment] = useState(null);
  const [dragOverSegment, setDragOverSegment] = useState(null);
  const [dropPosition, setDropPosition] = useState<'before' | 'after' | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false);
  const [rebuildLoading, setRebuildLoading] = useState(false);
  const [tripPreferences, setTripPreferences] = useState({
    country_id: null,
    city_ids: [],
    duration_in_days: null,
    budget: null,
    total_travelers: null,
    cuisine_type: [],
    activity_type: [],
    name: '',
    origin_airport: '',
    departure_airport: '',
    travel_mode: '',
    arrival_date: '',
    end_date: '',
    preserve_city_order: false
  });
  const [preferencesLookup, setPreferencesLookup] = useState({
    activity_types: [],
    cuisine_types: [],
    travel_modes: [],
    'trip-statuses': []
  });
  const [airports, setAirports] = useState([]);
  const [tripPricing, setTripPricing] = useState<any>(null);
  const [pricingLoading, setPricingLoading] = useState<boolean>(false);
  const [pricingDialogOpen, setPricingDialogOpen] = useState<boolean>(false);
  const [addPOIDialogOpen, setAddPOIDialogOpen] = useState<boolean>(false);
  const [shareTripDialogOpen, setShareTripDialogOpen] = useState<boolean>(false);
  const [manageSharesDialogOpen, setManageSharesDialogOpen] = useState<boolean>(false);
  const [availablePOIs, setAvailablePOIs] = useState<any[]>([]);
  const [loadingAvailablePOIs, setLoadingAvailablePOIs] = useState<boolean>(false);
  const [selectedDayForPOI, setSelectedDayForPOI] = useState<number | null>(null);
  const [addingPOI, setAddingPOI] = useState<boolean>(false);
  const [dismissedWarnings, setDismissedWarnings] = useState<Set<number>>(new Set());

  // Google Maps API loading
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSaveTrip = async () => {
    handleMenuClose();

    if (!isAuthenticated) {
      setSnackbarMessage('Please log in to save trips');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    const tripId = tripData?.id;
    if (!tripId) {
      setSnackbarMessage('Unable to save trip: Trip ID not found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    setSaveLoading(true);
    setLoading(true);
    try {
      // Save trip and get the new saved trip data
      const savedTripData = await api.saveTripToCustomer(tripId);

      // Fetch new segments with the new ID
      const newTripId = savedTripData?.id;
      if (newTripId) {
        // Fetch complete trip details including image_url
        const [completeTrip, segmentsData] = await Promise.all([
          api.fetchTripById(newTripId),
          api.fetchTripSegments(newTripId)
        ]);

        const countryId = completeTrip.country_id || savedTripData.country_id || tripData.country_id || 2;

        // Fetch POIs for the country
        const poisData = await api.fetchPOIsByCountry(countryId);

        // Update current trip data with complete trip details (includes image_url)
        // User owns saved trips, so can_edit is always true
        setCurrentTripData({ ...completeTrip, can_edit: true });
        setSegments(segmentsData);
        setPois(poisData);
      }

      // Track trip saved
      const savedTripId = savedTripData?.id || tripId;
      analytics.trackTripSaved(savedTripId);

      setSnackbarMessage('Trip saved to My Trips!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Mark trip as saved and dismiss prompt
      setIsTripSaved(true);
      setSavePromptDismissed(true);

      // Trip is no longer a system trip after saving - make it editable
      setIsCurrentlySystemTrip(false);

      // Notify parent component
      if (onTripSaved) {
        onTripSaved();
      }
    } catch (err) {
      console.error('Error saving trip:', err);

      if (err instanceof AuthenticationError) {
        setLoginPromptOpen(true);
      } else {
        setSnackbarMessage('Failed to save trip. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setSaveLoading(false);
      setLoading(false);
    }
  };

  const handleShareTrip = () => {
    handleMenuClose();
    setShareTripDialogOpen(true);
  };

  const handleDownloadPdf = async () => {
    handleMenuClose();
    try {
      const tripName = tripData?.name || tripData?.title;
      await api.downloadTripPdf(tripData.id, tripName);
      setSnackbarMessage('PDF downloaded successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      setSnackbarMessage('Failed to download PDF');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEditTrip = () => {
    handleMenuClose();
    setIsEditMode(!isEditMode);
  };

  const handleOpenPreferences = () => {
    handleMenuClose();
    setPreferencesDialogOpen(true);
  };

  const handleClosePreferences = () => {
    setPreferencesDialogOpen(false);
  };

  const handleRebuildTrip = async (newPreferences: typeof tripPreferences) => {
    setRebuildLoading(true);
    setLoading(true);
    try {
      // Build new trip with preferences (creates a new trip)
      // Exclude arrival_date and end_date from the API call
      const { arrival_date, end_date, ...preferencesToSend } = newPreferences;

      // Ensure country_id and city_ids are included from current trip data
      const rebuildRequest = {
        ...preferencesToSend,
        country_id: preferencesToSend.country_id || currentTripData?.country_id || null,
        city_ids: preferencesToSend.city_ids?.length > 0
          ? preferencesToSend.city_ids
          : (currentTripData?.city_ids || currentTripData?.cities?.map(c => c.id) || [])
      };

      const newTripData = await api.rebuildTrip(rebuildRequest);

      // Update current trip data with the new trip, preserving can_edit
      setCurrentTripData({ ...newTripData, can_edit: true });

      // Fetch segments and POIs for the new trip
      const newTripId = newTripData?.id;
      if (newTripId) {
        const countryId = newTripData.country_id || tripPreferences.country_id || 2;

        // Fetch segments and POIs for the new trip
        const [segmentsData, poisData, citiesData] = await Promise.all([
          api.fetchTripSegments(newTripId),
          api.fetchPOIsByCountry(countryId),
          api.fetchCities()
        ]);

        setSegments(segmentsData);
        setPois(poisData);

        // Update cities based on new trip
        let selectedCities = [];
        if (newTripData.selected_cities && Array.isArray(newTripData.selected_cities)) {
          selectedCities = newTripData.selected_cities;
        } else if (newTripData.cities && Array.isArray(newTripData.cities)) {
          selectedCities = newTripData.cities;
        } else if (newTripData.city_ids && Array.isArray(newTripData.city_ids)) {
          selectedCities = citiesData.filter(city => newTripData.city_ids.includes(city.id));
        }
        setCities(selectedCities);

        // Automatically save to My Trips if user is authenticated
        if (isAuthenticated) {
          try {
            const savedTripData = await api.saveTripToCustomer(newTripId);

            // Update current trip data with the saved trip, user owns it so can_edit is true
            setCurrentTripData({ ...savedTripData, can_edit: true });

            // Fetch new segments with the saved trip's ID
            const savedTripId = savedTripData?.id;
            if (savedTripId) {
              const savedSegmentsData = await api.fetchTripSegments(savedTripId);
              setSegments(savedSegmentsData);
            }

            // Mark trip as saved
            setIsTripSaved(true);
            setSavePromptDismissed(true);

            // Notify parent component
            if (onTripSaved) {
              onTripSaved();
            }

            // Track trip rebuilt
            analytics.trackTripRebuilt(savedTripId);

            setSnackbarMessage('Trip rebuilt and saved to My Trips!');
          } catch (saveErr) {
            console.error('Error saving rebuilt trip:', saveErr);
            setSnackbarMessage('Trip rebuilt successfully, but failed to save. Please save manually.');
            setIsTripSaved(false);
            setSavePromptDismissed(false);
          }
        } else {
          setSnackbarMessage('New trip created successfully!');
          setIsTripSaved(false);
          setSavePromptDismissed(false);
        }
      } else {
        setSnackbarMessage('New trip created successfully!');
        setIsTripSaved(false);
        setSavePromptDismissed(false);
      }

      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setPreferencesDialogOpen(false);
    } catch (err) {
      console.error('Error creating new trip:', err);

      if (err instanceof AuthenticationError) {
        setPreferencesDialogOpen(false);
        setLoginPromptOpen(true);
      } else if (err instanceof TripBuildError) {
        setSnackbarMessage(err.message);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Failed to create new trip. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setRebuildLoading(false);
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Add POI handlers
  const handleOpenAddPOI = async (day: number) => {
    setSelectedDayForPOI(day);
    setLoadingAvailablePOIs(true);
    setAddPOIDialogOpen(true);

    try {
      // Find all segments for this day
      const daySegments = segments?.filter((s: any) => parseInt(s.day) === day) || [];
      if (daySegments.length === 0) {
        setSnackbarMessage('No segments found for this day to determine city.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        setAddPOIDialogOpen(false);
        return;
      }

      // Get all unique city IDs for this day
      const cityIds: number[] = Array.from(new Set(daySegments.map((s: any) => s.city_id).filter(Boolean)));
      if (cityIds.length === 0) {
        setSnackbarMessage('Could not determine city for this day.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        setAddPOIDialogOpen(false);
        return;
      }

      // Fetch POIs from all cities for this day
      const allCityPOIsPromises = cityIds.map(cityId => api.fetchPOIsByCity(cityId));
      const allCityPOIsResults = await Promise.all(allCityPOIsPromises);

      // Combine POIs from all cities and remove duplicates by ID
      const combinedPOIs = allCityPOIsResults.flat();
      const uniquePOIs = Array.from(
        new Map(combinedPOIs.map(poi => [poi.id, poi])).values()
      );

      // Get POI IDs already in the entire trip (all days)
      const existingPOIIds = new Set(
        segments
          ?.filter((s: any) => s.segment_type === 'poi_exploration' && s.poi_id)
          .map((s: any) => s.poi_id) || []
      );

      // Filter out POIs already in the trip
      const available = uniquePOIs.filter(poi => !existingPOIIds.has(poi.id));

      setAvailablePOIs(available);
    } catch (error) {
      console.error('Error fetching available POIs:', error);
      setSnackbarMessage('Failed to load available POIs.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingAvailablePOIs(false);
    }
  };

  const handleCloseAddPOI = () => {
    setAddPOIDialogOpen(false);
    setAvailablePOIs([]);
    setSelectedDayForPOI(null);
  };

  const handleAddPOI = async (poi: any) => {
    if (!selectedDayForPOI || !currentTripData) return;

    setAddingPOI(true);
    try {
      const tripId = currentTripData?.id;

      // Get the city_id from the POI itself, or fall back to the day's segments
      const daySegments = segments?.filter((s: any) => parseInt(s.day) === selectedDayForPOI) || [];
      const cityId = poi.city_id || daySegments[0]?.city_id;

      if (!cityId) {
        setSnackbarMessage('Could not determine city for this POI.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setAddingPOI(false);
        return;
      }

      // Create a poi_exploration segment
      await api.addSegmentToTrip(tripId, {
        segment_type: 'poi_exploration',
        poi_id: poi.id,
        city_id: cityId,
        day: selectedDayForPOI,
        position: daySegments.length || 0
      });

      // Refresh segments
      const updatedSegments = await api.fetchTripSegments(tripId);
      setSegments(updatedSegments);

      // Refresh POIs to get the new POI data (using country_id from trip data)
      if (currentTripData?.country_id) {
        const updatedPOIs = await api.fetchPOIsByCountry(currentTripData.country_id);
        setPois(updatedPOIs);
      }

      // Track POI added
      analytics.trackPOIAdded(poi.id, poi.name);

      setSnackbarMessage(`Added "${poi.name}" to Day ${selectedDayForPOI}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      handleCloseAddPOI();
    } catch (error) {
      console.error('Error adding POI:', error);
      setSnackbarMessage('Failed to add POI to trip.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setAddingPOI(false);
    }
  };

  // Edit trip name handlers
  const handleStartEditName = () => {
    setEditedTripName(currentTripData?.title || currentTripData?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedTripName('');
  };

  const handleSaveTripName = async () => {
    const tripId = currentTripData?.id;
    if (!tripId) {
      setSnackbarMessage('Unable to update trip: Trip ID not found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!editedTripName.trim()) {
      setSnackbarMessage('Trip name cannot be empty');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setUpdateLoading(true);
    try {
      // Send full trip payload with updated name
      const fullPayload = {
        name: editedTripName,
        image_url: currentTripData?.image_url || currentTripData?.banner_url,
        origin_airport: currentTripData?.origin_airport,
        departure_airport: currentTripData?.departure_airport,
        travel_mode: currentTripData?.travel_mode,
        duration_in_days: currentTripData?.duration_in_days,
        budget: currentTripData?.budget,
        total_travelers: currentTripData?.total_travelers,
        cuisine_type: currentTripData?.cuisine_type,
        activity_type: currentTripData?.activity_type,
        arrival_date: currentTripData?.arrival_date
      };

      await api.updateTrip(tripId, fullPayload);

      // Update local state
      setCurrentTripData({
        ...currentTripData,
        name: editedTripName,
        title: editedTripName
      });

      setSnackbarMessage('Trip name updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setIsEditingName(false);
    } catch (err) {
      console.error('Error updating trip name:', err);

      if (err instanceof AuthenticationError) {
        setIsEditingName(false);
        setLoginPromptOpen(true);
      } else {
        setSnackbarMessage('Failed to update trip name. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTripName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  // Image upload handler
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const tripId = currentTripData?.id;
    if (!tripId) {
      setSnackbarMessage('Unable to update image: Trip ID not found');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSnackbarMessage('Please select a valid image file');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSnackbarMessage('Image size must be less than 5MB');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setImageUploadLoading(true);
    try {
      // Step 1: Upload image to Google Cloud Storage
      const uploadResult = await api.uploadImageToGCS(file, 'trips');

      if (uploadResult.status !== 200 || !uploadResult.url) {
        throw new Error('Failed to upload image to cloud storage');
      }

      // Step 2: Send full trip payload with updated image URL
      const fullPayload = {
        name: currentTripData?.name || currentTripData?.title,
        image_url: uploadResult.url,
        origin_airport: currentTripData?.origin_airport,
        departure_airport: currentTripData?.departure_airport,
        travel_mode: currentTripData?.travel_mode,
        duration_in_days: currentTripData?.duration_in_days,
        budget: currentTripData?.budget,
        total_travelers: currentTripData?.total_travelers,
        cuisine_type: currentTripData?.cuisine_type,
        activity_type: currentTripData?.activity_type,
        arrival_date: currentTripData?.arrival_date
      };

      await api.updateTrip(tripId, fullPayload);

      // Step 3: Update local state with the new image URL
      setCurrentTripData({
        ...currentTripData,
        image_url: uploadResult.url,
        banner_url: uploadResult.url
      });

      setSnackbarMessage('Trip image updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error uploading trip image:', err);

      if (err instanceof AuthenticationError) {
        setLoginPromptOpen(true);
      } else {
        setSnackbarMessage('Failed to update trip image. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setImageUploadLoading(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  // Keep currentTripData in sync with tripData prop
  useEffect(() => {
    if (tripData) {
      // Preserve can_edit status when syncing from prop, default to true for user's own trips
      setCurrentTripData((prev) => ({
        ...tripData,
        can_edit: tripData.can_edit !== undefined ? tripData.can_edit : prev?.can_edit ?? true,
      }));
    }
  }, [tripData]);

  // Reset dismissed warnings when trip data changes (e.g., after rebuild)
  useEffect(() => {
    setDismissedWarnings(new Set());
  }, [currentTripData?.id]);

  // Set initial saved state based on prop
  useEffect(() => {
    setIsTripSaved(isSavedTrip);
  }, [isSavedTrip]);

  // Initialize trip preferences from trip data
  useEffect(() => {
    if (currentTripData) {
      setTripPreferences({
        country_id: currentTripData.country_id || null,
        city_ids: currentTripData.city_ids || currentTripData.cities?.map(c => c.id) || [],
        duration_in_days: currentTripData.duration_in_days || null,
        budget: currentTripData.budget || null,
        total_travelers: currentTripData.total_travelers || null,
        cuisine_type: currentTripData.cuisine_type || [],
        activity_type: currentTripData.activity_type || [],
        name: currentTripData.name || currentTripData.title || '',
        origin_airport: currentTripData.origin_airport || '',
        departure_airport: currentTripData.departure_airport || '',
        travel_mode: currentTripData.travel_mode || '',
        arrival_date: currentTripData.arrival_date || '',
        end_date: currentTripData.end_date || '',
        preserve_city_order: currentTripData.preserve_city_order || false
      });
    }
  }, [currentTripData]);

  // Fetch trip preferences lookup data on mount
  useEffect(() => {
    const fetchPreferencesData = async () => {
      try {
        const data = await api.fetchTripPreferences();
        setPreferencesLookup(data);
      } catch (err) {
        console.error('Error fetching preferences lookup data:', err);
      }
    };

    fetchPreferencesData();
  }, []);

  // Fetch airports data on mount
  useEffect(() => {
    const fetchAirportsData = async () => {
      try {
        const data = await api.fetchAirports();
        setAirports(data);
      } catch (err) {
        console.error('Error fetching airports data:', err);
      }
    };

    fetchAirportsData();
  }, []);

  // Show success toast for newly created trips (not for system trips or saved trips)
  useEffect(() => {
    // Never show success message for system trips or saved trips
    if (isCurrentlySystemTrip || isSavedTrip) {
      setShowSuccessMessage(false);
      return;
    }

    if (showSuccessMessage) {
      setSnackbarMessage('Trip built successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setShowSuccessMessage(false); // Only show once
    }
  }, [showSuccessMessage, isSavedTrip, isCurrentlySystemTrip]);

  useEffect(() => {
    const fetchData = async () => {
      if (!tripData) {
        setLoading(false);
        return;
      }

      try {
        // Extract trip ID and country ID from the response
        const tripId = tripData.id;
        const countryId = tripData.country_id || 2; // Default to 2 if not provided

        if (!tripId) {
          throw new Error('No trip ID found in response');
        }

        // Fetch segments, POIs, and cities first (fast calls)
        const [segmentsData, poisData, citiesData] = await Promise.all([
          api.fetchTripSegments(tripId),
          api.fetchPOIsByCountry(countryId),
          api.fetchCities()
        ]);

        // Use the cities that the user actually selected in the trip wizard
        let selectedCities = [];

        // Check if tripData contains selected cities
        if (tripData.selected_cities && Array.isArray(tripData.selected_cities)) {
          selectedCities = tripData.selected_cities;
        } else if (tripData.cities && Array.isArray(tripData.cities)) {
          selectedCities = tripData.cities;
        } else if (tripData.city_ids && Array.isArray(tripData.city_ids)) {
          selectedCities = citiesData.filter(city => tripData.city_ids.includes(city.id));
        } else {
          selectedCities = citiesData.filter(city => String(city.country_id) === String(countryId));
        }

        setSegments(segmentsData);
        setPois(poisData);
        setCities(selectedCities);
        setLoading(false);

        // Fetch pricing in background (slow call) - don't block UI
        setPricingLoading(true);
        api.fetchTripPricing(tripId)
          .then(pricingData => {
            setTripPricing(pricingData);
          })
          .catch(err => {
            console.error('Error fetching pricing:', err);
          })
          .finally(() => {
            setPricingLoading(false);
          });

      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [tripData]);

  // Hide success message after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSuccessMessage(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  const enrichSegmentWithPOI = useCallback((segment) => {
    // Handle city_travel segments
    if (segment.segment_type === 'city_travel' && segment.meta_data) {
      const { source_city_id, destination_city_id } = segment.meta_data;

      let sourceCity = null;
      let destinationCity = null;

      if (cities && Array.isArray(cities)) {
        sourceCity = cities.find(c => c.id === source_city_id);
        destinationCity = cities.find(c => c.id === destination_city_id);
      }

      const sourceName = sourceCity?.name || `City ${source_city_id}`;
      const destinationName = destinationCity?.name || `City ${destination_city_id}`;

      return {
        ...segment,
        name: `Travel from ${sourceName} to ${destinationName}`,
        description: `City transfer`,
        type: 'city_travel',
        isCityTravel: true,
        images: [],
        mainImage: null,
        fullDetails: {
          source_city_id,
          destination_city_id,
          sourceCity,
          destinationCity
        }
      };
    }

    // Handle dinner and lunch segments - check multiple possible field names
    if (segment.segment_type === 'dinner' || segment.segment_type === 'lunch' ||
        segment.type === 'dinner' || segment.type === 'lunch' ||
        segment.name?.toLowerCase().includes('dinner') ||
        segment.name?.toLowerCase().includes('lunch')) {

      const mealType = segment.segment_type || segment.type ||
        (segment.name?.toLowerCase().includes('dinner') ? 'dinner' : 'lunch');

      // Get restaurant options from meta_data
      const restaurantOptionsRaw = segment.meta_data?.restaurant_options || [];

      // Look up restaurant POIs - handle both old format (array of IDs) and new format (array of objects)
      const restaurantPOIs = restaurantOptionsRaw
        .map(option => {
          // New format: { poi_id, distance_meters, distance_from }
          if (typeof option === 'object' && option.poi_id) {
            const poi = pois?.find(p => p.id === option.poi_id);
            if (poi) {
              return {
                ...poi,
                distance_meters: option.distance_meters,
                distanceFrom: option.distance_from || null
              };
            }
            return null;
          }
          // Old format: just the ID
          return pois?.find(p => p.id === option);
        })
        .filter(Boolean);

      if (restaurantPOIs.length > 0) {
        return {
          ...segment,
          name: `${mealType.charAt(0).toUpperCase() + mealType.slice(1)} Options`,
          description: restaurantPOIs.map(r => r.name).join(' or '),
          type: mealType,
          isMeal: true,
          images: [], // No images for meal segments - show icon instead
          mainImage: null,
          restaurantOptions: restaurantPOIs,
          fullDetails: restaurantPOIs[0]
        };
      } else {
        return {
          ...segment,
          name: segment.name || `${mealType.charAt(0).toUpperCase() + mealType.slice(1)} Option`,
          description: `${mealType} restaurant`,
          type: mealType,
          isMeal: true,
          images: [], // No images for meal segments - show icon instead
          mainImage: null,
          restaurantOptions: [],
          fullDetails: null
        };
      }
    }

    // Handle regular POI segments
    if (!pois || !Array.isArray(pois)) {
      return segment;
    }

    // Try to find matching POI by ID or name
    const matchingPOI = pois.find(poi => 
      poi.id === segment.poi_id || 
      poi.id === segment.point_of_interest_id ||
      poi.name === segment.point_of_interest ||
      poi.name === segment.name
    );

    if (matchingPOI) {
      const poiImages = matchingPOI.images || (matchingPOI.image_url ? [matchingPOI.image_url] : []);
      const processedImages = poiImages.filter(Boolean);

      return {
        ...segment,
        name: matchingPOI.name || segment.name,
        description: matchingPOI.description || segment.description,
        address: matchingPOI.address || segment.address,
        type: matchingPOI.type || segment.type,
        image_url: matchingPOI.image_url || segment.image_url,
        images: processedImages,
        mainImage: processedImages[0] || null,
        fullDetails: matchingPOI
      };
    }

    return segment;
  }, [pois, cities]);

  const handleOpenDialog = useCallback((segment) => {
    setSelectedPOI(segment);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedPOI(null);
  }, []);

  const handleItineraryDaySelect = useCallback((day) => {
    setSelectedItineraryDay(day);
  }, []);

  const handleDeleteSegmentClick = (segment: any) => {
    setSegmentToDelete(segment);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteSegmentConfirm = async () => {
    if (!segmentToDelete) return;

    try {
      // Call API to delete segment
      await api.deleteSegment(segmentToDelete.id);

      // Refresh segments
      const tripId = currentTripData?.id;
      if (tripId) {
        const updatedSegments = await api.fetchTripSegments(tripId);
        setSegments(updatedSegments);

        // Track POI/segment removed
        analytics.trackPOIRemoved(segmentToDelete.poi_id || segmentToDelete.id);

        setSnackbarMessage('Segment deleted successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error deleting segment:', err);

      if (err instanceof api.AuthenticationError) {
        setLoginPromptOpen(true);
      } else {
        setSnackbarMessage('Failed to delete segment. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setDeleteConfirmOpen(false);
      setSegmentToDelete(null);
    }
  };

  const handleDeleteSegmentCancel = () => {
    setDeleteConfirmOpen(false);
    setSegmentToDelete(null);
  };

  const handleDragStart = (e: React.DragEvent, segment: any) => {
    setDraggedSegment(segment);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', (e.currentTarget as HTMLElement).innerHTML);
    (e.currentTarget as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDraggedSegment(null);
    setDragOverSegment(null);
    setDropPosition(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, segment: any, segmentIndex: number, daySegments: any[]) => {
    if (draggedSegment && draggedSegment.id !== segment.id) {
      setDragOverSegment(segment);

      // Determine if we should show line before or after this segment
      const draggedIndex = daySegments.findIndex(s => s.id === draggedSegment.id);
      if (draggedIndex < segmentIndex) {
        // Dragging downward, show line after the target
        setDropPosition('after');
      } else {
        // Dragging upward, show line before the target
        setDropPosition('before');
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the card entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverSegment(null);
      setDropPosition(null);
    }
  };

  const handleDrop = async (e: React.DragEvent, dropTarget: any) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedSegment || draggedSegment.id === dropTarget.id) {
      setDraggedSegment(null);
      setDragOverSegment(null);
      return;
    }

    try {
      // Call API to reorder - source is the dragged segment, dest is where we're dropping
      await api.reorderSegments(draggedSegment.id, dropTarget.id);

      // Refresh segments
      const tripId = currentTripData?.id;
      if (tripId) {
        const updatedSegments = await api.fetchTripSegments(tripId);
        setSegments(updatedSegments);

        setSnackbarMessage('Segment order updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error reordering segment:', err);

      if (err instanceof api.AuthenticationError) {
        setLoginPromptOpen(true);
      } else {
        setSnackbarMessage('Failed to reorder segment. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setDraggedSegment(null);
      setDragOverSegment(null);
      setDropPosition(null);
    }
  };

  const handleMoveSegment = async (segment: any, direction: 'up' | 'down') => {
    // Find the current day's segments
    const daySegments = segments.filter(s => s.day === segment.day);
    const currentIndex = daySegments.findIndex(s => s.id === segment.id);

    if (currentIndex === -1) return;

    // Determine target segment
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= daySegments.length) return;

    const targetSegment = daySegments[targetIndex];

    try {
      // Call API to reorder
      await api.reorderSegments(segment.id, targetSegment.id);

      // Refresh segments
      const tripId = currentTripData?.id;
      if (tripId) {
        const updatedSegments = await api.fetchTripSegments(tripId);
        setSegments(updatedSegments);

        setSnackbarMessage('Segment order updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (err) {
      console.error('Error reordering segment:', err);

      if (err instanceof api.AuthenticationError) {
        setLoginPromptOpen(true);
      } else {
        setSnackbarMessage('Failed to reorder segment. Please try again.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // Callback for when route data is ready from TripMap
  const handleRouteDataReady = useCallback((data: RouteData) => {
    setRouteData(data);
  }, []);

  // Memoize maps to prevent re-rendering when dialog state changes
  const memoizedTripMap = useMemo(() => {
    if (mapView === 'trip') {
      return <TripMap cities={cities} segments={segments} isLoaded={isLoaded} loadError={loadError} onRouteDataReady={handleRouteDataReady} />;
    }
    return null;
  }, [mapView, cities, segments, isLoaded, loadError, handleRouteDataReady]);

  const memoizedPOIMap = useMemo(() => {
    if (mapView === 'poi') {
      return <POIMap pois={pois} segments={segments} selectedDay={selectedItineraryDay} isLoaded={isLoaded} loadError={loadError} cities={cities} onPOIClick={handleOpenDialog} enrichSegmentWithPOI={enrichSegmentWithPOI} />;
    }
    return null;
  }, [mapView, pois, segments, selectedItineraryDay, isLoaded, loadError, cities, handleOpenDialog, enrichSegmentWithPOI]);

  const renderTripByDays = (segmentsData) => {
    if (!segmentsData || !Array.isArray(segmentsData)) {
      return <Typography>No segments data available</Typography>;
    }

    // Group segments by day and enrich with POI data
    const segmentsByDay = {};

    segmentsData.forEach(segment => {
      const enrichedSegment = enrichSegmentWithPOI(segment);
      const day = enrichedSegment.day || enrichedSegment.day_number || 1;
      if (!segmentsByDay[day]) {
        segmentsByDay[day] = [];
      }
      segmentsByDay[day].push(enrichedSegment);
    });

    // Sort segments within each day by their position field
    Object.keys(segmentsByDay).forEach(day => {
      segmentsByDay[day].sort((a, b) => (a.position || 0) - (b.position || 0));
    });

    // Sort days and render
    const sortedDays = Object.keys(segmentsByDay).sort((a, b) => parseInt(a) - parseInt(b));

    // Get cities for selected day
    const getSelectedDayCities = () => {
      if (!selectedItineraryDay || !segmentsByDay[selectedItineraryDay]) return [];
      const dayCityIds = Array.from(new Set(segmentsByDay[selectedItineraryDay].map(seg => seg.city_id).filter(Boolean)));
      return dayCityIds
        .map(cityId => cities.find(c => c.id === cityId))
        .filter(Boolean)
        .map(c => c.name);
    };

    return (
      <Box sx={{ mb: 3 }}>
        {/* Day Pills and Edit Button */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center' }}>
          <DaySelector
            days={sortedDays}
            selectedDay={selectedItineraryDay}
            segmentsByDay={segmentsByDay}
            onDaySelect={handleItineraryDaySelect}
          />
          {!isCurrentlySystemTrip && currentTripData?.can_edit !== false && (
            <DayActionsToolbar
              isEditMode={isEditMode}
              selectedDay={selectedItineraryDay}
              onEditModeToggle={() => setIsEditMode(!isEditMode)}
              onAddPOI={handleOpenAddPOI}
            />
          )}
        </Box>

        {/* Day Details */}
        {selectedItineraryDay && segmentsByDay[selectedItineraryDay] && (
          <>
            <DayHeader
              day={selectedItineraryDay}
              cities={getSelectedDayCities()}
            />
            <DaySegmentsList
              segments={segmentsByDay[selectedItineraryDay]}
              isEditMode={isEditMode}
              draggedSegment={draggedSegment}
              dragOverSegment={dragOverSegment}
              dropPosition={dropPosition}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onDeleteSegment={handleDeleteSegmentClick}
              onOpenDialog={handleOpenDialog}
              onMoveSegment={handleMoveSegment}
            />
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>Error: {error}</Alert>
        <Typography variant="h6" gutterBottom>Trip Summary</Typography>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="body1">
              Your trip has been successfully planned! Trip ID: {tripData?.id || 'Unknown'}
            </Typography>
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" component="pre" sx={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word',
                fontSize: '0.875rem'
              }}>
                {JSON.stringify(tripData, null, 2)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      {/* Trip Header with Hero Image */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: 250, sm: 350, md: 400 },
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${currentTripData?.image_url || currentTripData?.banner_url || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80'})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          color: 'white',
        }}
      >
        {/* Image Upload Button - only show if can edit */}
        {currentTripData?.can_edit !== false && (
          <>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              id="trip-image-upload"
              onChange={handleImageUpload}
            />
            <label htmlFor="trip-image-upload">
              <IconButton
                component="span"
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
                disabled={imageUploadLoading}
              >
                {imageUploadLoading ? <CircularProgress size={24} color="inherit" /> : <PhotoCameraIcon />}
              </IconButton>
            </label>
          </>
        )}

        {/* Top Bar with Back Button and Menu */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Box>
            {onBack && (
              <IconButton
                onClick={onBack}
                aria-label="back to trips"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Trip Title and Info */}
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Compact Save Prompt */}
          {!isTripSaved && !savePromptDismissed && isAuthenticated && (
            <Box
              sx={{
                mb: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                px: 2,
                py: 1,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              <SaveIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.9)' }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontSize: '0.875rem',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                {isCurrentlySystemTrip ? 'Like this trip?' : 'Trip not saved'}
              </Typography>
              <Button
                onClick={handleSaveTrip}
                disabled={saveLoading}
                size="small"
                variant="contained"
                startIcon={saveLoading ? <CircularProgress size={14} color="inherit" /> : null}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'white',
                  },
                  boxShadow: 1
                }}
              >
                {saveLoading ? 'Adding...' : 'Add to My Trips'}
              </Button>
              <IconButton
                onClick={() => setSavePromptDismissed(true)}
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  p: 0.5,
                  ml: -0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {/* Login prompt for non-authenticated users */}
          {!isAuthenticated && !savePromptDismissed && (
            <Box
              sx={{
                mb: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: 2,
                px: 2,
                py: 1,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}
            >
              <LoginIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.9)' }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'white',
                  fontSize: '0.875rem',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                }}
              >
                Log in to save or customize this trip
              </Typography>
              <Button
                onClick={() => setLoginDialogOpen(true)}
                size="small"
                variant="contained"
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  py: 0.5,
                  px: 1.5,
                  minWidth: 'auto',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'white',
                  },
                  boxShadow: 1
                }}
              >
                Log In
              </Button>
              <IconButton
                onClick={() => setSavePromptDismissed(true)}
                size="small"
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  p: 0.5,
                  ml: -0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, mb: 2, flexWrap: 'wrap' }}>
            {isEditingName ? (
              <>
                <InputBase
                  value={editedTripName}
                  onChange={(e) => setEditedTripName(e.target.value)}
                  onKeyDown={handleNameKeyDown}
                  autoFocus
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' },
                    color: 'white',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                    minWidth: { xs: 200, sm: 300 },
                    '& input': {
                      padding: 0,
                      color: 'white',
                    }
                  }}
                />
                <IconButton
                  onClick={handleSaveTripName}
                  disabled={updateLoading}
                  sx={{
                    bgcolor: 'rgba(76, 175, 80, 0.8)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      bgcolor: 'rgba(76, 175, 80, 0.9)',
                    },
                    width: 40,
                    height: 40
                  }}
                >
                  {updateLoading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                </IconButton>
                <IconButton
                  onClick={handleCancelEditName}
                  disabled={updateLoading}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)',
                    },
                    width: 40,
                    height: 40
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 'bold',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '3rem' }
                  }}
                >
                  {currentTripData?.title || currentTripData?.name || 'Your Trip'}
                </Typography>
                {!isCurrentlySystemTrip && currentTripData?.can_edit !== false && (
                  <IconButton
                    onClick={handleStartEditName}
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      backdropFilter: 'blur(10px)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)',
                      },
                      width: 40,
                      height: 40
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </>
            )}
          </Box>
          {currentTripData?.description && (
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                maxWidth: 800,
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
              }}
            >
              {currentTripData.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {tripData?.is_shared && (
              <Chip
                label={tripData.can_edit ? 'Shared' : 'Shared (View Only)'}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  fontWeight: 'bold',
                }}
              />
            )}
            {currentTripData?.shared_with && currentTripData.shared_with.length > 0 && (
              <Chip
                icon={<GroupIcon />}
                label={`Shared with ${currentTripData.shared_with.length} ${currentTripData.shared_with.length === 1 ? 'person' : 'people'}`}
                onClick={() => setManageSharesDialogOpen(true)}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(25, 118, 210, 0.9)',
                    color: 'white',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  },
                  '& .MuiChip-icon': {
                    color: 'primary.main'
                  }
                }}
              />
            )}
            {currentTripData?.created_at && (
              <Chip
                label={`Created: ${new Date(currentTripData.created_at).toLocaleDateString()}`}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                  fontWeight: 'bold',
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Content Section */}
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
      {/* Trip Warnings */}
      {(() => {
        const warnings = currentTripData?.warnings || tripData?.warnings || [];
        const visibleWarnings = warnings.filter((_: any, index: number) => !dismissedWarnings.has(index));
        if (visibleWarnings.length === 0) return null;
        return (
          <Box sx={{ mb: 3 }}>
            {warnings.map((warning: any, index: number) => {
              if (dismissedWarnings.has(index)) return null;
              // Handle both string warnings and {code, message} object warnings
              const warningMessage = typeof warning === 'string' ? warning : warning?.message || 'Warning';
              return (
                <Alert
                  key={index}
                  severity="warning"
                  sx={{ mb: 1 }}
                  onClose={() => {
                    setDismissedWarnings(prev => {
                      const next = new Set(prev);
                      next.add(index);
                      return next;
                    });
                  }}
                >
                  {warningMessage}
                </Alert>
              );
            })}
          </Box>
        );
      })()}

      {/* Combined Map Panel with Toggle */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={mapView === 'trip' ? 0 : 1}
            onChange={(e, newValue) => setMapView(newValue === 0 ? 'trip' : 'poi')}
            aria-label="map view tabs"
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 'auto',
                padding: '6px 16px',
                backgroundColor: 'transparent !important',
                '&.Mui-selected': {
                  color: 'primary.main',
                  backgroundColor: 'transparent !important'
                },
                '&:not(.Mui-selected)': {
                  color: 'text.secondary',
                  backgroundColor: 'transparent !important'
                },
                '&:hover': {
                  color: 'primary.main',
                  backgroundColor: 'transparent !important'
                },
                '&:focus': {
                  backgroundColor: 'transparent !important'
                },
                '&:active': {
                  backgroundColor: 'transparent !important'
                }
              }
            }}
          >
            <Tab
              icon={<RouteIcon />}
              label="Trip Route"
              iconPosition="start"
              disableRipple
            />
            <Tab
              icon={<PlaceIcon />}
              label="Points of Interest"
              iconPosition="start"
              disableRipple
            />
          </Tabs>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {currentTripData?.duration_in_days > 0 && (
              <Button
                onClick={() => setRouteDialogOpen(true)}
                startIcon={<TravelExploreIcon />}
                disableRipple
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  padding: '6px 16px',
                  minWidth: 'auto',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                {currentTripData.duration_in_days} Days
              </Button>
            )}
            {pricingLoading && (
              <Button
                startIcon={<CircularProgress size={24} sx={{ color: 'inherit' }} />}
                disabled
                disableRipple
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  padding: '6px 16px',
                  minWidth: 'auto',
                  '&.Mui-disabled': {
                    color: 'text.secondary',
                  },
                }}
              >
                Calculating...
              </Button>
            )}
            {!pricingLoading && tripPricing && Number(tripPricing.total_cost) > 0 && (
              <Button
                onClick={() => setPricingDialogOpen(true)}
                startIcon={<AttachMoneyIcon />}
                disableRipple
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  padding: '6px 16px',
                  minWidth: 'auto',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Est. ${tripPricing.total_cost.toLocaleString()}
              </Button>
            )}
            <IconButton
              onClick={handleMenuOpen}
              aria-label="trip actions"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {/* For system trips: show Add to My Trips option */}
              {isCurrentlySystemTrip && isAuthenticated && !isTripSaved && (
                <MenuItem onClick={handleSaveTrip} disabled={saveLoading}>
                  <SaveIcon sx={{ mr: 1 }} fontSize="small" />
                  {saveLoading ? 'Adding...' : 'Add to My Trips'}
                </MenuItem>
              )}
              {/* For non-system trips (user's own trips): show Save option if not saved */}
              {!isCurrentlySystemTrip && isAuthenticated && !isTripSaved && (
                <MenuItem onClick={handleSaveTrip} disabled={saveLoading}>
                  <SaveIcon sx={{ mr: 1 }} fontSize="small" />
                  {saveLoading ? 'Saving...' : 'Save to My Trips'}
                </MenuItem>
              )}
              {/* Edit options only for non-system trips with edit permission */}
              {!isCurrentlySystemTrip && currentTripData?.can_edit !== false && (
                <MenuItem onClick={handleEditTrip}>
                  <EditIcon sx={{ mr: 1 }} fontSize="small" />
                  {isEditMode ? 'Done Editing' : 'Edit Trip'}
                </MenuItem>
              )}
              {!isCurrentlySystemTrip && currentTripData?.can_edit !== false && selectedItineraryDay && (
                <MenuItem onClick={() => {
                  handleMenuClose();
                  handleOpenAddPOI(parseInt(selectedItineraryDay));
                }}>
                  <AddIcon sx={{ mr: 1 }} fontSize="small" />
                  Add POI to Trip
                </MenuItem>
              )}
              {!isCurrentlySystemTrip && currentTripData?.can_edit !== false && (
                <MenuItem onClick={handleOpenPreferences}>
                  <BuildIcon sx={{ mr: 1 }} fontSize="small" />
                  Rebuild Trip
                </MenuItem>
              )}
              {/* Share only for customer trips or saved trips with edit permission */}
              {(!isCurrentlySystemTrip || isTripSaved) && currentTripData?.can_edit !== false && (
                <MenuItem onClick={handleShareTrip}>
                  <ShareIcon sx={{ mr: 1 }} fontSize="small" />
                  Share Trip
                </MenuItem>
              )}
              <MenuItem onClick={handleDownloadPdf}>
                <PictureAsPdfIcon sx={{ mr: 1 }} fontSize="small" />
                Download PDF
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Trip Map */}
        {memoizedTripMap}

        {/* Points of Interest Map */}
        {memoizedPOIMap}
      </Box>

      {renderTripByDays(segments)}
      </Box>

      {/* POI Details Dialog */}
      <POIDetailsDialog
        open={dialogOpen}
        poi={selectedPOI}
        onClose={handleCloseDialog}
      />

      {/* Login Prompt Dialog */}
      <AuthRequiredDialog
        open={loginPromptOpen}
        onClose={() => setLoginPromptOpen(false)}
        onLogin={() => {
          setLoginPromptOpen(false);
          setLoginDialogOpen(true);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteSegmentDialog
        open={deleteConfirmOpen}
        segmentName={segmentToDelete?.name || 'this segment'}
        onCancel={handleDeleteSegmentCancel}
        onConfirm={handleDeleteSegmentConfirm}
      />

      {/* Trip Pricing Dialog */}
      <TripPricingDialog
        open={pricingDialogOpen}
        loading={pricingLoading}
        pricing={tripPricing}
        totalTravelers={currentTripData?.total_travelers}
        onClose={() => setPricingDialogOpen(false)}
      />

      {/* Trip Preferences Dialog */}
      <RebuildTripDialog
        open={preferencesDialogOpen}
        preferences={tripPreferences}
        preferencesLookup={preferencesLookup}
        airports={airports}
        loading={rebuildLoading}
        onClose={handleClosePreferences}
        onRebuild={handleRebuildTrip}
      />

      {/* Add POI Dialog */}
      <AddPOIDialog
        open={addPOIDialogOpen}
        selectedDay={selectedDayForPOI}
        availablePOIs={availablePOIs}
        loading={loadingAvailablePOIs}
        adding={addingPOI}
        onClose={handleCloseAddPOI}
        onAddPOI={handleAddPOI}
      />

      {/* Share Trip Dialog */}
      <ShareTripDialog
        open={shareTripDialogOpen}
        onClose={() => {
          setShareTripDialogOpen(false);
          // Refresh trip data to get updated shared_with list, preserving can_edit status
          const tripId = currentTripData?.id || tripData?.id;
          if (tripId) {
            api.fetchTripById(tripId).then((data) => {
              setCurrentTripData({
                ...data,
                can_edit: data.can_edit !== undefined ? data.can_edit : currentTripData?.can_edit ?? true,
              });
            }).catch(console.error);
          }
        }}
        tripId={currentTripData?.id || tripData?.id}
        tripName={currentTripData?.name || currentTripData?.title || tripData?.name || tripData?.title}
      />

      {/* Manage Shares Dialog */}
      <ManageSharesDialog
        open={manageSharesDialogOpen}
        onClose={() => {
          setManageSharesDialogOpen(false);
          // Refresh trip data to get updated shared_with list, preserving can_edit status
          const id = currentTripData?.id || tripData?.id;
          if (id) {
            api.fetchTripById(id).then((data) => {
              setCurrentTripData({
                ...data,
                can_edit: data.can_edit !== undefined ? data.can_edit : currentTripData?.can_edit ?? true,
              });
            }).catch(console.error);
          }
        }}
        tripName={currentTripData?.name || currentTripData?.title || tripData?.name || tripData?.title}
        sharedWith={currentTripData?.shared_with || []}
        onSharesUpdated={() => {
          // Refresh trip data to get updated shared_with list
          const id = currentTripData?.id || tripData?.id;
          if (id) {
            api.fetchTripById(id).then(setCurrentTripData).catch(console.error);
          }
        }}
      />

      {/* Login Dialog */}
      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
        onSuccess={() => {
          setLoginDialogOpen(false);
          onLoginSuccess();
        }}
        onRegisterClick={() => {
          setLoginDialogOpen(false);
          setRegisterDialogOpen(true);
        }}
        onForgotPasswordClick={() => {
          setLoginDialogOpen(false);
          setForgotPasswordDialogOpen(true);
        }}
      />

      {/* Register Dialog */}
      <RegisterDialog
        open={registerDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
        onSuccess={() => {
          setRegisterDialogOpen(false);
          onLoginSuccess();
        }}
        onLoginClick={() => {
          setRegisterDialogOpen(false);
          setLoginDialogOpen(true);
        }}
      />

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={forgotPasswordDialogOpen}
        onClose={() => setForgotPasswordDialogOpen(false)}
        onBackToLogin={() => {
          setForgotPasswordDialogOpen(false);
          setLoginDialogOpen(true);
        }}
      />

      {/* Route Info Dialog */}
      <Dialog
        open={routeDialogOpen}
        onClose={() => setRouteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <TravelExploreIcon sx={{ fontSize: 28, mr: 1, color: 'primary.main' }} />
            <Typography component="span" variant="h6" fontWeight="bold">
              Trip Overview
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Duration & Route Summary */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PlaceIcon color="action" />
                <Typography variant="body1">Duration</Typography>
              </Box>
              <Typography variant="body1" fontWeight="bold" color="primary.main">
                {currentTripData?.duration_in_days} Days
              </Typography>
            </Box>

            {routeData?.totalDistance && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsCarIcon color="action" />
                  <Typography variant="body1">Total Distance</Typography>
                </Box>
                <Typography variant="body1" fontWeight="medium">
                  {routeData.totalDistance} km
                </Typography>
              </Box>
            )}

            {routeData?.totalDuration && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RouteIcon color="action" />
                  <Typography variant="body1">Driving Time</Typography>
                </Box>
                <Typography variant="body1" fontWeight="medium">
                  {routeData.totalDuration} hours
                </Typography>
              </Box>
            )}
          </Box>

          {/* Route */}
          {routeData?.routeString && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Trip Route
              </Typography>
              <Typography variant="body1">
                {routeData.routeString}
              </Typography>
            </Box>
          )}

          {/* City-to-City Travel */}
          {routeData?.cityLegs && routeData.cityLegs.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                City-to-City Travel
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {routeData.cityLegs.map((leg, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      p: 1.5,
                      bgcolor: leg.failed ? 'warning.50' : 'grey.50',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DirectionsCarIcon
                        sx={{
                          fontSize: 20,
                          color: leg.failed ? 'warning.main' : 'action.active',
                        }}
                      />
                      <Typography variant="body2">
                        {leg.from} → {leg.to}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="medium" color={leg.failed ? 'warning.main' : 'text.primary'}>
                      {leg.failed ? 'No route' : `${leg.distance} km • ${leg.duration} hrs`}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* No route data message */}
          {!routeData && (
            <Typography variant="body2" color="text.secondary">
              Route information is being calculated...
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default TripItinerary; 